import { css } from '@emotion/react'
import { graphql } from 'gatsby'
import { ComponentPropsWithoutRef } from 'react'

import { GatsbyImageFocused } from '@/features/common/'
import { absoluteFill, mq } from '@/theme/mixins'
import { colors } from '@/theme/variables'

interface Props extends ComponentPropsWithoutRef<'section'> {
  divisionName?: Queries.DivisionHeroFragment['divisionName'] | null
  heroImages?: Queries.DivisionHeroFragment['heroImages'] | null
}

export const DivisionHero = ({
  divisionName,
  heroImages,
  ...props
}: Props): JSX.Element => {
  const styles = {
    section: css`
      background-color: #fff;
      --padding: 0.5rem;
      position: relative;
      display: grid;
      padding: var(--padding);
      gap: var(--padding);
      grid-template-columns: 1fr auto 1fr;
      grid-template-rows: 0.9fr auto 1fr;
      min-height: clamp(40vw, (100svh - var(--nav-height)), 75vw);
      box-sizing: border-box;
      ${mq().ms} {
        grid-template-columns: 1fr 1fr 1fr;
        min-height: clamp(100vw, (100svh - var(--nav-height)), 125vw);
      }
      ${mq().s} {
        gap: 0.333rem;
      }
    `,
    image: css`
      ${absoluteFill}
      &:nth-of-type(1) {
        grid-column: 1 / 2;
        grid-row: 1 / 4;
        ${mq().ms} {
          grid-row: 1 / 2;
        }
      }
      &:nth-of-type(2) {
        grid-column: 2 / 3;
        grid-row: 1 / 2;
        ${mq().ms} {
          grid-column: 2 / 4;
        }
      }
      &:nth-of-type(3) {
        grid-column: 3 / 4;
        grid-row: 1 / 3;
        ${mq().ms} {
          grid-column: 3 / 4;
          grid-row: 3 / 4;
        }
      }
      &:nth-of-type(4) {
        grid-column: 2 / 4;
        grid-row: 3 / 4;
        ${mq().ms} {
          grid-column: 1 / 3;
        }
      }
    `,
    heading: css`
      grid-column: 2 / 3;
      grid-row: 2 / 3;
      font-size: var(--fs-60);
      color: ${colors.gray10};
      text-transform: uppercase;
      margin: 0;
      padding: 0.75em 1em;
      line-height: 1;
      min-width: 33vw;
      text-align: center;
      ${mq().ms} {
        grid-column: 1 / 4;
        padding: 0.667em 1rem;
      }
    `,
  }
  return (
    <section
      css={styles.section}
      {...props}
    >
      {heroImages?.map((image, i) => (
        <GatsbyImageFocused
          image={i % 2 ? image.landscape : image.portrait}
          aspectRatio={i % 2 ? 2 : 0.8}
          key={i}
          css={styles.image}
          loading="eager"
          data={image}
        />
      ))}
      <h1 css={styles.heading}>{divisionName}</h1>
    </section>
  )
}

export const DivisionHeroFragment = graphql`
  fragment DivisionHero on DatoCmsDivisionPage {
    divisionName
    heroImages {
      portrait: gatsbyImageData(
        width: 720
        sizes: "33vw"
        imgixParams: { ar: "4:5", fit: "crop", crop: "focalpoint" }
      )
      landscape: gatsbyImageData(
        width: 1280
        sizes: "67vw"
        imgixParams: { ar: "2:1", fit: "crop", crop: "focalpoint" }
      )
      ...ImageFocalData
    }
  }
`
